import React, { useEffect, useState } from "react";
import {
  Box, Typography, Grid, Chip, Button, TextField, Table, TableHead,
  TableBody, TableRow, TableCell, TableContainer, Dialog, CircularProgress,
  IconButton, LinearProgress, Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InventoryIcon from "@mui/icons-material/Inventory";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNotifier } from "../../../Core/Notifier";
import ProcurementPremiumHeader from "../Shared/PremiumHeader";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();

const ProcurementReceiving: React.FC = () => {
  const navigate = useNavigate();
  const { showMessage, showErrorMessage } = useNotifier();
  const [pos, setPOs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Receive dialog
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState<any>(null);
  const [receiveQtys, setReceiveQtys] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState(false);

  const getHeaders = () => {
    const t = localStorage.getItem("token") || "";
    return { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` };
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/warehouse/pending-receiving`, { headers: getHeaders() });
      const json = await res.json();
      if (json.success) setPOs(json.data || []);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);

  const openReceive = (po: any) => {
    setSelectedPO(po);
    const qtys: Record<number, string> = {};
    (po.items || []).forEach((item: any) => {
      const remaining = Math.max(0, item.qty - (item.receivedQty || 0));
      qtys[item.id] = String(remaining);
    });
    setReceiveQtys(qtys);
    setReceiveOpen(true);
  };

  const handleReceive = async () => {
    if (!selectedPO) return;
    setSaving(true);
    try {
      const items = (selectedPO.items || [])
        .filter((item: any) => Number(receiveQtys[item.id] || 0) > 0)
        .map((item: any) => ({
          product_id: item.productId,
          received_qty: Number(receiveQtys[item.id] || 0),
        }));

      if (items.length === 0) { showErrorMessage("No items to receive"); setSaving(false); return; }

      const res = await fetch(`${API_URL}/warehouse/receive-stock`, {
        method: "POST", headers: getHeaders(),
        body: JSON.stringify({ po_id: selectedPO.id, po_number: selectedPO.billNo, items }),
      });
      const json = await res.json();
      if (json.success) {
        showMessage(`Received ${json.received_count} item(s) into inventory`);
        setReceiveOpen(false);
        loadData();
      } else showErrorMessage(json.message || "Failed to receive");
    } catch { showErrorMessage("Error receiving stock"); } finally { setSaving(false); }
  };

  const totalRemainingItems = pos.reduce((s, po: any) => s + Math.max(0, (po.total_items || 0) - (po.items_received || 0)), 0);
  const completionPct = (() => {
    const totalItems = pos.reduce((s, po: any) => s + (po.total_items || 0), 0);
    const totalReceived = pos.reduce((s, po: any) => s + (po.items_received || 0), 0);
    return totalItems > 0 ? Math.round((totalReceived / totalItems) * 100) : 0;
  })();

  return (
    <Box sx={{ bgcolor: "#FAFAFA", minHeight: "100vh", p: 3 }}>
      <ProcurementPremiumHeader
        title="Receiving Hub"
        subtitle="Quick-receive goods against pending Purchase Orders — stock updates instantly"
        icon={<LocalShippingIcon />}
        badge="LIVE"
        onRefresh={loadData}
        actions={[{
          label: "View Movements",
          icon: <InventoryIcon sx={{ fontSize: 16 }} />,
          variant: "outline",
          onClick: () => navigate("/inventory/movements"),
        }]}
        kpis={[
          { label: "Pending POs",          value: pos.filter(p => p.receiving_status === "pending").length, sub: "Awaiting first receipt",        icon: <LocalShippingIcon />, tone: "gold",    urgent: pos.filter(p => p.receiving_status === "pending").length > 0 },
          { label: "Partially Received",   value: pos.filter(p => p.receiving_status === "partial").length, sub: "In progress",                   icon: <WarningAmberIcon />,  tone: "blue" },
          { label: "Items Remaining",      value: totalRemainingItems,                                       sub: "Across all open POs",          icon: <InventoryIcon />,     tone: "purple" },
          { label: "Total POs",            value: pos.length,                                                sub: `${completionPct}% completion`,  icon: <CheckCircleIcon />,  tone: "emerald" },
        ]}
      />

      <Box>
        {/* KPI strip moved into header */}

        {/* PO Cards */}
        {loading ? (
          <Box sx={{ textAlign: "center", py: 8 }}><CircularProgress size={28} sx={{ color: "#00838f" }} /></Box>
        ) : pos.length === 0 ? (
          <Box sx={{ bgcolor: "#fff", borderRadius: 3, border: "1px solid #E5E7EB", textAlign: "center", py: 8 }}>
            <CheckCircleIcon sx={{ fontSize: 48, color: "#BBF7D0", mb: 1 }} />
            <Typography fontSize={16} fontWeight={600} color="#111827">All caught up!</Typography>
            <Typography fontSize={13} color="#9CA3AF">No pending deliveries to receive</Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {pos.map((po: any) => {
              const progress = po.total_items > 0 ? (po.items_received / po.total_items) * 100 : 0;
              const isPending = po.receiving_status === "pending";
              return (
                <Box key={po.id} sx={{ bgcolor: "#fff", borderRadius: "12px", border: "1px solid #E5E7EB", overflow: "hidden", transition: "border-color 0.18s", "&:hover": { borderColor: "#cbd5e1" } }}>
                  {/* PO Header */}
                  <Box sx={{ px: 3, py: 2, display: "flex", alignItems: "center", gap: 2, borderBottom: "1px solid #F3F4F6" }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                        <Typography fontSize={15} fontWeight={700} color="#111827">
                          {po.billNo || `PO-${po.id}`}
                        </Typography>
                        <Chip label={isPending ? "Pending" : "Partial"} size="small"
                          sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: isPending ? "#FFFBEB" : "#EFF6FF", color: isPending ? "#D97706" : "#2563EB" }} />
                      </Box>
                      <Typography fontSize={12} color="#6B7280">
                        {po.supplier_name}{" "}
                        {po.expected_delivery_date && (
                          <> &middot; Expected: <Typography component="span" fontSize={12} fontWeight={600} color="#374151">{new Date(po.expected_delivery_date).toLocaleDateString()}</Typography></>
                        )}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right", mr: 1 }}>
                      <Typography fontSize={10} color="#9CA3AF" fontWeight={500}>ITEMS</Typography>
                      <Typography fontSize={14} fontWeight={700} color="#111827">{po.items_received}/{po.total_items}</Typography>
                    </Box>
                    <Box sx={{ width: 60 }}>
                      <LinearProgress variant="determinate" value={progress}
                        sx={{ height: 6, borderRadius: 3, bgcolor: "#F3F4F6", "& .MuiLinearProgress-bar": { bgcolor: progress === 100 ? "#059669" : "#00838f", borderRadius: 3 } }} />
                    </Box>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Tooltip title="View PO Details">
                        <IconButton size="small" onClick={() => navigate(`/po-detail/${po.id}`)}
                          sx={{ bgcolor: "#F9FAFB", "&:hover": { bgcolor: "#F3F4F6" } }}>
                          <VisibilityIcon sx={{ fontSize: 16, color: "#6B7280" }} />
                        </IconButton>
                      </Tooltip>
                      <Button variant="contained" disableElevation size="small" onClick={() => openReceive(po)}
                        sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600, fontSize: 12, bgcolor: "#00838f", "&:hover": { bgcolor: "#006064" } }}>
                        Receive
                      </Button>
                    </Box>
                  </Box>

                  {/* Items preview */}
                  <Box sx={{ px: 3, py: 1.5, display: "flex", gap: 2, flexWrap: "wrap" }}>
                    {(po.items || []).slice(0, 5).map((item: any) => {
                      const remaining = Math.max(0, item.qty - (item.receivedQty || 0));
                      const done = remaining === 0;
                      return (
                        <Box key={item.id} sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: done ? "#BBF7D0" : "#FECACA" }} />
                          <Typography fontSize={11} color={done ? "#9CA3AF" : "#374151"} sx={{ textDecoration: done ? "line-through" : "none" }}>
                            {item.productName || `#${item.productId}`}
                          </Typography>
                          {!done && <Typography fontSize={10} color="#DC2626" fontWeight={600}>{remaining} left</Typography>}
                        </Box>
                      );
                    })}
                    {(po.items || []).length > 5 && <Typography fontSize={11} color="#9CA3AF">+{po.items.length - 5} more</Typography>}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Receive Dialog */}
      <Dialog open={receiveOpen} onClose={() => setReceiveOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #E5E7EB", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography fontSize={16} fontWeight={600} color="#111827">Receive: {selectedPO?.billNo || `PO-${selectedPO?.id}`}</Typography>
            <Typography fontSize={12} color="#9CA3AF">{selectedPO?.supplier_name}</Typography>
          </Box>
          <IconButton size="small" onClick={() => setReceiveOpen(false)}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
        </Box>
        <Box sx={{ p: 0 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#F9FAFB" }}>
                  {["Product", "Ordered", "Already Received", "Remaining", "Receive Now"].map(h => (
                    <TableCell key={h} sx={{ fontSize: 10, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(selectedPO?.items || []).map((item: any) => {
                  const remaining = Math.max(0, item.qty - (item.receivedQty || 0));
                  return (
                    <TableRow key={item.id} sx={{ bgcolor: remaining === 0 ? "#F9FAFB" : "transparent" }}>
                      <TableCell>
                        <Typography fontSize={13} fontWeight={600} color={remaining === 0 ? "#9CA3AF" : "#111827"}>
                          {item.productName || `#${item.productId}`}
                        </Typography>
                        <Typography fontSize={10} color="#9CA3AF">Current stock: {item.current_stock ?? "—"}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>{item.qty}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: "#059669", fontWeight: 600 }}>{item.receivedQty || 0}</TableCell>
                      <TableCell>
                        <Typography fontSize={12} fontWeight={600} color={remaining > 0 ? "#DC2626" : "#059669"}>{remaining}</Typography>
                      </TableCell>
                      <TableCell>
                        {remaining > 0 ? (
                          <TextField size="small" type="number" value={receiveQtys[item.id] || ""}
                            onChange={e => setReceiveQtys(prev => ({ ...prev, [item.id]: String(Math.min(Number(e.target.value), remaining)) }))}
                            inputProps={{ min: 0, max: remaining, step: 1 }}
                            sx={{ width: 80, "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 12, "& fieldset": { borderColor: "#E5E7EB" } } }} />
                        ) : (
                          <Chip label="Done" size="small" sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: "#F0FDF4", color: "#059669" }} />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Box sx={{ px: 3, py: 2, bgcolor: "#F9FAFB", borderTop: "1px solid #E5E7EB", display: "flex", gap: 1, justifyContent: "space-between", alignItems: "center" }}>
          <Button variant="text" size="small" onClick={() => {
            const qtys: Record<number, string> = {};
            (selectedPO?.items || []).forEach((item: any) => {
              qtys[item.id] = String(Math.max(0, item.qty - (item.receivedQty || 0)));
            });
            setReceiveQtys(qtys);
          }} sx={{ textTransform: "none", fontSize: 12, color: "#00838f" }}>
            Fill All Remaining
          </Button>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" size="small" onClick={() => setReceiveOpen(false)}
              sx={{ borderRadius: 1.5, textTransform: "none", borderColor: "#D1D5DB", color: "#374151" }}>Cancel</Button>
            <Button variant="contained" disableElevation size="small" onClick={handleReceive} disabled={saving}
              startIcon={saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 14 }} />}
              sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600, bgcolor: "#00838f", "&:hover": { bgcolor: "#006064" } }}>
              {saving ? "Receiving..." : "Confirm Receive"}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
};

export default ProcurementReceiving;
